import { ActivatedRouteSnapshot, Resolve, Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { UserProvider } from './utils/services/user.provider';
import { BackUrlService } from './utils/services/back-url.service';
import { Title } from '@angular/platform-browser';
import { ToastService } from './utils/components/toast/toast.service';
import {
    Section, SectionContent, Event, Project, Question, Course, Exercise, CheatSheet, Media, CampProvider,
    ScormModule, Lti, CourseProvider, SectionContentSession, QuestionSession, Camp, Video, Exchange, Certificate,
    CourseSubscriptionSession, CourseSubscriptionSessionProvider, SelfAssessment, StatisticsProvider, CompanyProvider
} from '@stuplay';
import { NavigationProvider } from './utils/services/navigation.provider';
import { Observable, throwError, of, lastValueFrom } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { DataHelper } from './utils/helpers';
import { TranslationService } from './utils/services/translation.service';
import { StorageService } from './utils/services/storage.service';
import { CookieService } from 'ngx-cookie-service';

@Injectable()
export class UserResolver implements Resolve<any> {
    constructor(
        private userProvider: UserProvider,
    ) { }

    resolve(route: ActivatedRouteSnapshot): Observable<any> {
        return this.userProvider.getCurrentUser();
    }
}

@Injectable()
export class CampResolver implements Resolve<Camp[]> {
    constructor(private campProvider: CampProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<Camp[]> {
        return this.campProvider
            .getCamps()
            .pipe(
                map((camps) => camps)
            );
    }
}

@Injectable()
export class CourseResolver implements Resolve<Course> {
    public theme: any;
    constructor(
        private courseProvider: CourseProvider,
        private navigation: NavigationProvider,
        private titleService: Title,
        private translate: TranslationService,
        private toastService: ToastService,
        private router: Router,
        private backUrlService: BackUrlService,
        private statisticsProvider: StatisticsProvider,
        private storageService: StorageService,
        private companyProvider: CompanyProvider,
        private cookieService: CookieService
    ) {}

        // calculos colors
     /**
      * adjustLightness — MÉTODO LEGADO, ya no se usa para generar la escala de colores.
      *
      * Sumaba/restaba un porcentaje a cada canal RGB para simular aclarado/oscurecido.
      * El problema es que la operación no preserva el tono (hue) del color original,
      * produciendo resultados incorrectos (colores con cast azul/rojo en los extremos).
      *
      * La escala 100–900 ahora la genera CSS con color-mix(in srgb, ...) en main.scss,
      * que sí preserva el tono al mezclar en el espacio de color sRGB.
      *
      * Se mantiene el método por si algún otro lugar del código lo usa, pero NO debe
      * llamarse desde setTheme().
      */
     private adjustLightness(color: string, percent: number): string {
        const hex = color.replace('#', '');
        const num = parseInt(hex, 16);

        let r = (num >> 16) & 0xff;
        let g = (num >> 8) & 0xff;
        let b = num & 0xff;

        // Ajustar la luminosidad sumando el porcentaje a cada componente de color
        r = Math.min(255, Math.max(0, r + Math.round((255 - r) * percent / 100)));
        g = Math.min(255, Math.max(0, g + Math.round((255 - g) * percent / 100)));
        b = Math.min(255, Math.max(0, b + Math.round((255 - b) * percent / 100)));

        return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0').toUpperCase()}`;
    }

    /**
     * setTheme — Aplica el tema de colores del cliente en runtime (white-labeling).
     *
     * Lee los colores de marca (primary / secondary) desde el colorTemplate de la compañía
     * y los escribe como variables CSS en el elemento <html> con setProperty().
     *
     * IMPORTANTE — Por qué solo se sobreescriben los tokens base (500):
     *   La escala completa 100–900 se calcula automáticamente en main.scss mediante
     *   color-mix(in srgb, var(--v2-color-primary-500), white/black X%).
     *   Si se escribieran los shades individuales aquí (como se hacía antes), el style
     *   inline del elemento tendría mayor especificidad que los estilos de hoja y taparía
     *   los valores correctos calculados por CSS.
     *
     * FLUJO:
     *   1. Se obtiene companyData del storageService (cargado previamente por CompanyGuard).
     *   2. Se extrae primaryColor y secondaryColor del colorTemplate.
     *   3. Se sobreescriben --v2-color-primary-500 y --v2-color-secondary-500 → CSS recalcula toda la escala.
     *   4. Se actualizan los alias legacy --primary-color / --secondary-color para componentes
     *      que aún usan el formato antiguo (sidebar, navigation, etc.).
     */
    public async setTheme(): Promise<void> {
        // Usar datos ya cargados por CompanyGuard en storageService (evita 404 por cookie vacía)
        let companyData: any = this.storageService.get('company');
        if (!companyData?.experience?.colorTemplate) {
            const companyId = this.cookieService.get('current_company') || sessionStorage.getItem('current_company');
            if (!companyId) { return; }
            companyData = await lastValueFrom(this.companyProvider.getCompany(companyId, { includes: 'roles,experience' }));
        }
        this.theme = companyData;
        const primaryColor = (this.theme.experience.colorTemplate.primaryColor === "#ffffff" || this.theme.experience.colorTemplate.primaryColor === "#fff")? "#949494" : this.theme.experience.colorTemplate.primaryColor;
        const secondaryColor = (this.theme.experience.colorTemplate.secondaryColor === "#ffffff" || this.theme.experience.colorTemplate.secondaryColor === "#fff")? "#949494" : this.theme.experience.colorTemplate.secondaryColor;

        // ─── Generación de la escala de colores ───────────────────────────────
        // ANTES: se calculaban los shades 100–900 con adjustLightness() (suma RGB)
        // y se escribían uno a uno con setProperty(). Ese algoritmo producía
        // resultados incorrectos (ej: primary-900 → #000016, secondary-900 → #ED0000)
        // porque el desplazamiento RGB no conserva el tono original del color.
        // Además, al escribir los valores con setProperty() en el style inline del
        // <html>, tenían mayor especificidad que cualquier hoja de estilos y
        // sobreescribían la escala correcta calculada por color-mix() en main.scss.
        //
        // AHORA: solo se sobreescribe el token base (--v2-color-*-500) y CSS
        // deriva toda la escala automáticamente con color-mix(in srgb, ...) desde main.scss.
        // Ventajas:
        //   • La escala es visualmente correcta (sRGB preserva el tono).
        //   • No hay riesgo de que JS tape los valores calculados por CSS.
        //   • Para cambiar el tema completo basta con 2 setProperty en vez de 18.
        // ─────────────────────────────────────────────────────────────────────

        // Set only the two base tokens — CSS color-mix() derives the full 100–900 scale automatically.
        // Setting individual shades here is not needed and would override the CSS-computed values.
        document.documentElement.style.setProperty('--v2-color-primary-500', primaryColor);
        document.documentElement.style.setProperty('--v2-color-secondary-500', secondaryColor);

        // Keep legacy aliases pointing to the new base (for old components still using --primary-color etc.)
        document.documentElement.style.setProperty('--primary-color', primaryColor);
        document.documentElement.style.setProperty('--secondary-color', secondaryColor);
        document.documentElement.style.setProperty('--background-color', primaryColor);
    }

    resolve(route: ActivatedRouteSnapshot): Observable<Course> {
        // Leer company desde storageService aquí (ya cargado por CompanyGuard)
        const company = this.storageService.get('company');
        const params = {
            preview: route.queryParams.preview ? 1 : 0,
            direct_play: route.queryParams.direct_play ? 1 : 0,
            company_id: route.queryParams.company_id || (company ? company.id : null),
            isFromLearningPath: !!route.queryParams.isFromLearningPath ? 1 : 0
        };

        return this.courseProvider
            .getCoursePlayer(parseInt(route.paramMap.get('courseId'), 10), params)
            .pipe(
                map((course) => {
                    this.backUrlService.setBackUrl();

                    const sections = course.sections;
                    for (const section of sections) {
                        section.sectionContents = section.sectionContents.filter((sectionContent) => !sectionContent.exercise || sectionContent.exercise.questions?.length > 0);
                    }

                    this.navigation.currentCourse = course;
                    course.title ? this.titleService.setTitle(course.title) : this.titleService.setTitle(this.translate.instant('player.untitled-course'));
                    this.storageService.getAndSet('course', course);
                    if (!params.preview) {
                        this.statisticsProvider.launchContent(course?.id, {
                            context: "course"
                        }).subscribe(() => {
                            this.setTheme();
                        });
                    }
                    return course;
                }),
                catchError((error: any) => {
                    this.router.navigate(['']);
                    if (error.status === 403) {
                        this.toastService.push('forbidden', 'error', 5000, 'camp');
                    } else if (error.status === 404) {
                        this.toastService.push('course-camp-not-found', 'error', 5000);
                    } else {
                        this.toastService.push('error-occurred', 'error', 5000);
                    }
                    return throwError(error);
                })
            )
    }
}

@Injectable()
export class CourseSubscriptionSessionResolver implements Resolve<CourseSubscriptionSession> {
    constructor(private courseSubscriptionSessionProvider: CourseSubscriptionSessionProvider, private navigation: NavigationProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<CourseSubscriptionSession> {
        return this.courseSubscriptionSessionProvider
            .getOrCreateCourseSubscriptionSession(parseInt(route.paramMap.get('courseId'), 10), route.queryParams.preview, route.queryParams.direct_play)
            .pipe(
                map((courseSubscriptionSession: any) => {
                    this.navigation.currentCourseSubscriptionSession = courseSubscriptionSession;
                    return courseSubscriptionSession;
                }),
                catchError((error: any) => {
                    window.location.href = document.referrer;
                    return throwError(error);
                })
            );
    }
}

@Injectable()
export class SectionResolver implements Resolve<Section> {
    section: Observable<Section>;

    constructor(private navigation: NavigationProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<Section> {
        this.navigation.currentSectionContent = null;
        this.navigation.currentQuestion = null;
        if (route.parent.data['course']) {
            const section = route.parent.data['course'].sections.find(({ id }) => id === +route.paramMap.get('sectionId'));
            this.navigation.currentSection = section;
            return of(section);
        } else {
            return of(null);
        }
    }
}

@Injectable()
export class ExerciseResolver implements Resolve<Exercise> {
    section: Section;
    exercise: Exercise;

    resolve(route: ActivatedRouteSnapshot): Observable<Exercise> {
        this.section = route.parent.data['section'];

        for (const data of this.section.sectionContents) {
            if (data.context === 'exercise' && data?.exercise?.id === +route.paramMap.get(data.context + 'Id')) {
                this.exercise = data['exercise'];
            }
        }
        return of(this.exercise);
    }
}

@Injectable()
export class QuestionResolver implements Resolve<Question> {
    section: Section;

    constructor(private navigation: NavigationProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<Question> {
        this.section = route.parent.parent.data['section'];

        for (const data of this.section.sectionContents) {
            if (data.context === 'exercise' && data?.exercise?.id === +route.paramMap.get(data.context + 'Id')) {
                for (const question of data.exercise.questions) {
                    if (question?.id === +route.paramMap.get('questionId')) {
                        this.navigation.currentSectionContent = data;
                        this.navigation.currentQuestion = question;
                        return of(question)
                    }
                }
            }
        }
    }
}

@Injectable()
export class SectionContentSessionResolver implements Resolve<SectionContentSession> {
    private courseSubscriptionSession: CourseSubscriptionSession;
    private section: Section;

    constructor(private statisticsProvider: StatisticsProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<SectionContentSession> {
        this.courseSubscriptionSession = route.parent.parent.data['courseSubscriptionSession'];
        this.section = route.parent.data['section'];
        const sectionContent = this.section.sectionContents.find((sectionContent: any) => {
            return sectionContent[DataHelper.changeCase(sectionContent.context, 'camel')]?.id === +route.paramMap.get(`${DataHelper.changeCase(sectionContent.context, 'camel')}Id`)
        });
        if (sectionContent) {
            let preview = route.queryParams.preview ? 1 : 0;
            return this.courseSubscriptionSession.getOrCreateSectionContentSession(sectionContent?.id)
                .pipe(
                    switchMap((sectionContentSession: any) => {
                        if (!preview) {
                            this.statisticsProvider.launchActivity(sectionContent?.id).subscribe();
                        }
                        return of(sectionContentSession)
                    })
                )
        } else {
            return throwError('Not Found');
        }
    }
}

@Injectable()
export class QuestionSessionResolver implements Resolve<QuestionSession> {
    sectionContentSession: SectionContentSession;

    resolve(route: ActivatedRouteSnapshot): Observable<QuestionSession> {
        this.sectionContentSession = route.parent.data['sectionContentSession'];
        return this.sectionContentSession.getOrCreateQuestionSession(+route.paramMap.get('questionId'));
    }
}

@Injectable()
export class MediaResolver implements Resolve<SectionContent> {
    media: Media;
    section: Section;
    sectionContent: SectionContent;

    constructor(private navigation: NavigationProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<SectionContent> {
        this.navigation.currentSectionContent = null;
        this.navigation.currentQuestion = null;
        this.section = route.parent.data['section'];
        for (const data of this.section.sectionContents) {
            if (data.context === 'media' && data?.media?.id === +route.paramMap.get(data.context + 'Id') && data?.id === +route.paramMap.get('contentId')) {
                this.navigation.currentSectionContent = data;
                this.sectionContent = data;
                this.media = data['media'];
            }
        }
        return of(this.sectionContent);
    }
}

@Injectable()
export class VideoResolver implements Resolve<SectionContent> {
    video: Video;
    section: Section;
    sectionContent: SectionContent;

    constructor(private navigation: NavigationProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<SectionContent> {
        this.navigation.currentSectionContent = null;
        this.navigation.currentQuestion = null;
        this.section = route.parent.data['section'];
        for (const data of this.section.sectionContents) {
            if (data.context === 'video' && data?.video?.id === +route.paramMap.get(data.context + 'Id')) {
                this.navigation.currentSectionContent = data;
                this.sectionContent = data;
                this.video = data['video'];
            }
        }
        return of(this.sectionContent);
    }
}

@Injectable()
export class CheatSheetResolver implements Resolve<SectionContent> {
    cheat: CheatSheet;
    section: Section;
    sectionContent: SectionContent;

    constructor(private navigation: NavigationProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<SectionContent> {
        this.navigation.currentSectionContent = null;
        this.navigation.currentQuestion = null;
        this.section = route.parent.data['section'];
        for (const data of this.section.sectionContents) {
            if (data.context === 'cheatsheet' && data?.cheatsheet?.id === +route.paramMap.get(data.context + 'Id')) {
                this.navigation.currentSectionContent = data;
                this.sectionContent = data;
                this.cheat = data['cheatsheet'];
            }
        }
        return of(this.sectionContent);
    }
}

@Injectable()
export class ScormResolver implements Resolve<SectionContent> {
    scorm: ScormModule;
    section: Section;
    sectionContent: SectionContent;

    constructor(private navigation: NavigationProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<SectionContent> {
        this.navigation.currentSectionContent = null;
        this.navigation.currentQuestion = null;
        this.section = route.parent.data['section'];
        for (const data of this.section.sectionContents) {
            if (data.context === 'scorm' && data?.scorm?.id === +route.paramMap.get(data.context + 'Id')) {
                this.navigation.currentSectionContent = data;
                this.sectionContent = data;
                this.scorm = data['scorm'];
            }
        }
        return of(this.sectionContent);
    }
}

@Injectable()
export class ScormCloudResolver implements Resolve<SectionContent> {
    section: Section;
    sectionContent: SectionContent;

    constructor(private navigation: NavigationProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<SectionContent> {
        this.navigation.currentSectionContent = null;
        this.navigation.currentQuestion = null;
        this.section = route.parent.data['section'];

        for (const data of this.section.sectionContents) {
            if (data.context === 'scormcloud' && data?.scormcloud?.id === +route.paramMap.get(data.context + 'Id')) {
                this.navigation.currentSectionContent = data;
                this.sectionContent = data;
            }
        }
        return of(this.sectionContent);
    }
}

@Injectable()
export class ExternalLInkResolver implements Resolve<SectionContent> {
    lti: Lti;
    section: Section;
    sectionContent: SectionContent;

    constructor(private navigation: NavigationProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<SectionContent> {
        this.navigation.currentSectionContent = null;
        this.navigation.currentQuestion = null;
        this.section = route.parent.data.section;

        for (const sectionContent of this.section.sectionContents) {
            if (sectionContent.context === 'external_link' && sectionContent.externalLink.id === parseInt(route.paramMap.get('externalLinkId'), 10)) {
                this.navigation.currentSectionContent = sectionContent;
                this.sectionContent = sectionContent;
                this.lti = sectionContent.externalLink;
            }
        }
        return of(this.sectionContent);
    }
}

@Injectable()
export class EventResolver implements Resolve<SectionContent> {
    event: Event;
    section: Section;
    sectionContent: SectionContent;

    constructor(private navigation: NavigationProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<SectionContent> {
        this.navigation.currentSectionContent = null;
        this.navigation.currentQuestion = null;
        this.section = route.parent.data['section'];
        for (const data of this.section.sectionContents) {
            if (data.context === 'event' && data?.event?.id === +route.paramMap.get(data.context + 'Id')) {
                this.navigation.currentSectionContent = data;
                this.sectionContent = data;
                this.event = data['event'];
            }
        }
        return of(this.sectionContent);
    }
}

@Injectable()
export class ProjectResolver implements Resolve<SectionContent> {
    project: Project;
    section: Section;
    sectionContent: SectionContent;

    constructor(private navigation: NavigationProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<SectionContent> {
        this.navigation.currentSectionContent = null;
        this.navigation.currentQuestion = null;
        this.section = route.parent.data['section'];
        for (const data of this.section.sectionContents) {
            if (data.context === 'project' && data?.project?.id === +route.paramMap.get(data.context + 'Id')) {
                this.navigation.currentSectionContent = data;
                this.sectionContent = data;
                this.project = data['project'];
            }
        }
        return of(this.sectionContent);
    }
}

@Injectable()
export class SelfAssessmentResolver implements Resolve<SectionContent> {
    selfAssessment: SelfAssessment;
    section: Section;
    sectionContent: SectionContent;

    constructor(private navigation: NavigationProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<SectionContent> {
        this.navigation.currentSectionContent = null;
        this.navigation.currentQuestion = null;
        this.section = route.parent.data['section'];
        for (const data of this.section.sectionContents) {
            if (data.context === 'selfassessment' && data?.selfassessment?.id === +route.paramMap.get(data.context + 'Id')) {
                this.navigation.currentSectionContent = data;
                this.sectionContent = data;
                this.selfAssessment = data['selfassessment'];
            }
        }
        return of(this.sectionContent);
    }
}

@Injectable()
export class ExchangeResolver implements Resolve<SectionContent> {
    exchange: Exchange;
    section: Section;
    sectionContent: SectionContent;

    constructor(private navigation: NavigationProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<SectionContent> {
        this.navigation.currentSectionContent = null;
        this.navigation.currentQuestion = null;
        this.section = route.parent.data['section'];
        for (const data of this.section.sectionContents) {
            if (data.context === 'exchange' && data?.exchange?.id === +route.paramMap.get(data.context + 'Id')) {
                this.navigation.currentSectionContent = data;
                this.sectionContent = data;
                this.exchange = data['exchange'];
            }
        }
        return of(this.sectionContent);
    }
}

@Injectable()
export class CertificateResolver implements Resolve<SectionContent> {
    certificate: Certificate;
    section: Section;
    sectionContent: SectionContent;

    constructor(private navigation: NavigationProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<SectionContent> {
        this.navigation.currentSectionContent = null;
        this.navigation.currentQuestion = null;
        this.section = route.parent.data['section'];
        for (const data of this.section.sectionContents) {
            if (data.context === 'certificate' && data?.certificate?.id === +route.paramMap.get(data.context + 'Id')) {
                this.navigation.currentSectionContent = data;
                this.sectionContent = data;
                this.certificate = data['certificate'];
            }
        }
        return of(this.sectionContent);
    }
}
